export type NarrativeSSEvent = 'open' | 'status' | 'done' | 'finish' | 'error' | 'keepalive' | 'keep-alive';

export interface NarrativeDonePayload {
  downloadUrl: string;
  narrativeVersionId: string | number;
}

export type NarrativeEventHandler = (event: NarrativeSSEvent, payload: unknown) => void;

export interface NarrativeStreamParams {
  url: string;
  accessToken: string;
  body: { tripProfileId: string; inputParams?: Record<string, unknown> };
  signal?: AbortSignal;
  onEvent?: NarrativeEventHandler;
}

export async function narrativeStream({
  url,
  body,
  accessToken,
  signal,
  onEvent,
}: NarrativeStreamParams): Promise<NarrativeDonePayload | null> {
  const cacheBustedUrl = url.includes('?') ? `${url}&t=${Date.now()}` : `${url}?t=${Date.now()}`;

  let result: NarrativeDonePayload | null = null;

  try {
    const res = await fetch(cacheBustedUrl, {
      method: 'POST',
      headers: {
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(body),
      signal,
    });

    if (!res.ok || !res.body) {
      const text = await res.text().catch(() => '');
      throw new Error(`Narrative SSE request failed: ${res.status} ${res.statusText} ${text}`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finished = false;

    const emit = (event: NarrativeSSEvent, data: unknown) => {
      try {
        onEvent?.(event, data);
      } catch {
        // no-op
      }
    };

    while (true) {
      if (finished) break;
      const { value, done } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;
      const frames = buffer.split('\n\n');
      buffer = frames.pop() ?? '';

      for (const frame of frames) {
        if (!frame.trim()) continue;

        let event: NarrativeSSEvent | undefined;
        let dataRaw = '';
        for (const line of frame.split('\n')) {
          if (line.startsWith('event:')) {
            event = line.slice(6).trim() as NarrativeSSEvent;
          } else if (line.startsWith('data:')) {
            dataRaw += (dataRaw ? '\n' : '') + line.slice(5).trim();
          }
        }

        if (!event) continue;

        let payload: unknown = dataRaw;
        try {
          payload = JSON.parse(dataRaw);
        } catch {
          // keep as string
        }

        emit(event, payload);

        if (event === 'done') {
          result = payload as NarrativeDonePayload;
        }

        if (event === 'finish' || event === 'error') {
          finished = true;
          break;
        }
      }

      if (finished) {
        try {
          await reader.cancel();
        } catch {
          // ignore
        }
        break;
      }
    }
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'name' in err && (err as { name?: string }).name !== 'AbortError') {
      throw err;
    }
  }

  return result;
}
